import { DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';

export interface Setting {
    delete_timestamp: string | null;
    value: SettingValue;
    auto_generated: boolean;
    update_timestamp: string | null;
    entity_type: EntityType;
    entity_id: string;
    key: string;
    id: string;
    account_id: string;
    creation_timestamp: string;
}

export interface CreateSetting {
    value?: SettingValue;
    entity_type?: EntityType;
    entity_id?: string;
    key?: string;
}

export interface UpdateSetting {
    null_fields?: string[];
    value?: SettingValue;
}

export interface SettingTemplateResult {
    [key: string]: SettingTemplate[];
}
export interface SettingTemplate {
    key: string;
    type: SettingFieldType;
    options?: string[];
}

export type SettingValue = string[] | number[] | string | boolean | number | null;

enum QueryParams {
    KEY = 'key',
    VALUE = 'value',
    USER_ID = 'user_id',
    ACCOUNT_ID = 'account_id',
    ENTITY_TYPE = 'entity_type',
    ENTITY_ID = 'entity_id'
}

export type SettingQueryParams = QueryParams & DefaultQueryParams;
export type SettingListResponse = PaginatedResponse<Setting>;

export type SettingFieldType =
    | 'any'
    | 'bool'
    | 'colli_workflow_id'
    | 'custom_role_id'
    | 'deceased_workflow_id'
    | 'document_id'
    | 'enum'
    | 'equiment_container_id'
    | 'equipment_workflow_id'
    | 'float'
    | 'int'
    | 'channels'
    | 'label_template_id'
    | 'language'
    | 'languages'
    | 'package_workflow_id'
    | 'order_workflow_id'
    | 'payment_terminal_id'
    | 'playlist_id'
    | 'printer_id'
    | 'product_root_category_id'
    | 'screen_type'
    | 'slot_id'
    | 'spot_id'
    | 'sso_provider'
    | 'str'
    | 'transaction_state'
    | 'transaction_states'
    | 'transaction_workflow_id'
    | 'user_id'
    | 'user_group_id'
    | 'user_workflow_id'
    | 'wizard_id';

export enum SettingKey {
    COLLI_WORKFLOW_ID = 'colli_workflow_id',
    DECEASED_WORKFLOW_ID = 'deceased_workflow_id',
    EQUIPMENT_WORKFLOW_ID = 'equipment_workflow_id',
    WIZARD_ID = 'wizard_id',
    PIN_CODE_LENGTH = 'pin_code_length',
    DEFAULT_CHANNELS = 'default_channels',
    AVAILABLE_LANGUAGES = 'available_languages',
    DEFAULT_LANGUAGE = 'default_language',
    DEFAULT_SPOT_ID = 'default_spot_id',
    DEFAULT_SSO_PROVIDER = 'default_sso_provider',
    DEFAULT_PLAYLIST_ID = 'default_playlist_id',
    DEFAULT_PRODUCT_ROOT_CATEGORY_ID = 'default_product_root_category_id',
    DEFAULT_SHOP_PRODUCT_ROOT_CATEGORY_ID = 'default_shop_product_root_category_id',
    TRANSACTION_WORKFLOW_ID = 'transaction_workflow_id',
    DEFAULT_PRINTER_ID = 'default_printer_id',
    UNKNOWN_EQUIPMENT_CONTAINER_ID = 'unknown_equipment_container_id',
    EULA_ACCEPTANCE_REQUIRED = 'eula_acceptance_required',
    OVERRIDE_EULA_URL = 'override_eula_url',
    PRIVACY_POLICY_DOCUMENT_ID = 'privacy_policy_document_id',
    LABEL_TEMPLATE_ID = 'label_template_id',
    BACKUP_NOTIFICATION_USER_ID = 'backup_notification_user_id',
    BACKUP_NOTIFICATION_ADDRESS_ENABLED = 'backup_notification_address_enabled',
    DEFAULT_RECEIVER_ID = 'default_receiver_id',
    DEFAULT_SENDER_ID = 'default_sender_id',
    HIDE_TELLOPORT_SUPPORT_INFORMATION = 'hide_telloport_support_information',
    ALLOW_PERSONAL_QR_ACCESS_CODE = 'allow_personal_qr_access_code',
    OPEN_DROP_OFF_SENDERS_USER_GROUP_ID = 'open_drop_off_senders_user_group_id',
    OPEN_DROP_OFF_RECEIVERS_USER_GROUP_ID = 'open_drop_off_receivers_user_group_id',
    ON_SCAN_REGEX_DATA_VALIDATION = 'on_scan_regex_data_validation',
    ORDER_WORKFLOW_ID = 'order_workflow_id',
    DROP_OFF_PACKAGE_WORKFLOW_ID = 'drop_off_package_workflow_id',
    PICK_UP_PACKAGE_WORKFLOW_ID = 'pick_up_package_workflow_id',
    DROP_OFF_MOVED_PACKAGE_WORKFLOW_ID = 'drop_off_moved_package_workflow_id',
    PICK_UP_MOVED_PACKAGE_WORKFLOW_ID = 'pick_up_moved_package_workflow_id',
    REMOVE_PACKAGE_WORKFLOW_ID = 'remove_package_workflow_id',
    ALLOW_USER_IDENTIFICATION = 'allow_user_identification',
    SHOW_USER_IDENTIFICATION_KEYBOARD = 'show_user_identification_keyboard',
    ALLOW_UNIDENTIFIED_EQUIPMENT_RETURN = 'allow_unidentified_equipment_return',
    ALLOW_CAROUSEL_INTERRUPT = 'allow_carousel_interrupt',
    HOME_SCREEN_TYPE = 'home_screen_type',
    DOMAIN = 'domain',
    PAYMENT_TERMINAL_ID = 'payment_terminal_id',
    ENFORCE_COST_CENTER_USE = 'enforce_cost_center_use',
    INACTIVITY_TIMEOUT_SECONDS = 'inactivity_timeout_seconds',
    INACTIVITY_TIMEOUT_MODAL_SECONDS = 'inactivity_timeout_modal_seconds',
    AUDIT_PACKAGE_WORKFLOW_ID = 'audit_package_workflow_id',
    REQUIRE_SIMILAR_SLOTS_ON_MOVE = 'require_similar_slots_on_move',
    REQUIRE_NON_FINAL_DESTINATION = 'require_non_final_destination',
    SKIP_VERIFY_SLOT_AVAILABILITY = 'skip_verify_slot_availability',
    DELAYED_RECEIVERS_SEARCH = 'delayed_receivers_search',
    STALE_TRANSACTION_DAYS = 'stale_transaction_days'
}
